import React, { useEffect, useState } from "react";
import { Button, Modal, Tag, Tooltip, message } from "antd";
import { useTranslation } from "react-i18next";
import { GlobalOutlined, InstagramOutlined, WhatsAppOutlined } from "@ant-design/icons";
import { launchEmbeddedSignup } from "../Sonic/whatsappEmbeddedSignup";
import { channelConnection, getChannelStatus, statusTagColor } from "./channelKind";
import {
  connectProspectWhatsapp,
  disconnectProspectInstagram,
  disconnectProspectWhatsapp,
  startProspectInstagramOAuth,
} from "./prospectService";
import styles from "./style.module.scss";

const ROWS = [
  { id: "web", icon: GlobalOutlined },
  { id: "instagram", icon: InstagramOutlined },
  { id: "whatsapp", icon: WhatsAppOutlined },
];

const ChannelsSection = ({
  bot,
  whatsappConfig,
  onOpenEmbed,
  onOpenSettings,
  onUpdated,
  variant = "settings",
  requestConnect = "",
  onRequestHandled,
}) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState("");
  const [leave, setLeave] = useState("");
  const [removeId, setRemoveId] = useState("");
  const dashboard = variant === "dashboard";

  const connectInstagram = async () => {
    if (!bot?.id) return;
    setBusy("instagram");
    try {
      const started = await startProspectInstagramOAuth(bot.id);
      if (started?.authorize_url) window.location.assign(started.authorize_url);
      else message.warning(t("sonic_prospect.ig_oauth_unconfigured"));
    } catch (_) {
      message.error(t("sonic_prospect.ig_connect_error"));
    } finally {
      setBusy("");
      setLeave("");
    }
  };

  const connectWhatsapp = async () => {
    const cfg = whatsappConfig || {};
    setBusy("whatsapp");
    try {
      const session = await launchEmbeddedSignup({
        appId: cfg.app_id,
        configId: cfg.config_id,
        graphVersion: cfg.graph_version,
      });
      if (!session?.phone_number_id) {
        message.error(t("sonic_prospect.wa_connect_error"));
        return;
      }
      const saved = await connectProspectWhatsapp(bot.id, {
        code: session.code,
        phoneNumberId: session.phone_number_id,
        wabaId: session.waba_id || "",
      });
      message.success(t("sonic_prospect.wa_connected"));
      if (onUpdated) onUpdated(saved);
    } catch (err) {
      if (err?.message !== "cancelled") message.error(t("sonic_prospect.wa_connect_error"));
    } finally {
      setBusy("");
      setLeave("");
    }
  };

  const removeConnection = async () => {
    if (!bot?.id || !removeId) return;
    setBusy(removeId);
    try {
      const saved =
        removeId === "whatsapp"
          ? await disconnectProspectWhatsapp(bot.id)
          : await disconnectProspectInstagram(bot.id);
      message.success(t("sonic_prospect.channel_removed_ok"));
      if (onUpdated) onUpdated(saved);
    } catch (_) {
      message.error(t("sonic_prospect.channel_remove_error"));
    } finally {
      setBusy("");
      setRemoveId("");
    }
  };

  const onAction = (id) => {
    const connection = channelConnection(bot, id);
    if (id === "web") {
      if (connection.state === "pending" && onOpenEmbed) onOpenEmbed();
      else if (onOpenSettings) onOpenSettings();
      else if (onOpenEmbed) onOpenEmbed();
      return;
    }
    if (connection.state === "connected") {
      setRemoveId(id);
      return;
    }
    if (id === "instagram") {
      setLeave("instagram");
      return;
    }
    if (!whatsappConfig?.configured || !whatsappConfig?.app_id) {
      message.warning(t("sonic_prospect.wa_unconfigured"));
      return;
    }
    setLeave("whatsapp");
  };

  useEffect(() => {
    if (!requestConnect) return undefined;
    onAction(requestConnect);
    if (onRequestHandled) onRequestHandled();
    return undefined;
  }, [requestConnect]);

  return (
    <section className={styles.channelsSection}>
      <h2>{t("sonic_prospect.channels_title")}</h2>
      <ul className={styles.channelList}>
        {ROWS.map((row) => {
          const Icon = row.icon;
          const status = getChannelStatus(bot, row.id);
          const connection = channelConnection(bot, row.id);
          const label = dashboard
            ? t(
                connection.state === "connected"
                  ? "sonic_prospect.channel_action_manage"
                  : connection.state === "pending"
                    ? "sonic_prospect.channel_action_finish"
                    : "sonic_prospect.channel_action_connect",
              )
            : row.id === "web"
              ? t(status === "live" ? "sonic_prospect.channel_manage" : "sonic_prospect.channel_get_code")
              : t(`sonic_prospect.channel_connect_${row.id}`);
          return (
            <li key={row.id}>
              <span className={styles.channelName}>
                <Icon />
                <strong>{t(`sonic_prospect.channel_name_${row.id}`)}</strong>
              </span>
              <Tag color={statusTagColor(status)}>
                {t(`sonic_prospect.channel_status_${status}`)}
              </Tag>
              {dashboard && connection.state === "connected" && connection.value ? (
                <span className={styles.channelValue}>{connection.value}</span>
              ) : null}
              <Button type="primary" loading={busy === row.id} onClick={() => onAction(row.id)}>
                {label}
              </Button>
            </li>
          );
        })}
      </ul>
      <Modal
        open={Boolean(leave)}
        title={t(leave === "whatsapp" ? "sonic_prospect.wa_leave_title" : "sonic_prospect.ig_oauth_leave_title")}
        okText={t(leave === "whatsapp" ? "sonic_prospect.wa_leave_ok" : "sonic_prospect.ig_oauth_leave_ok")}
        cancelText={t("sonic_prospect.confirm_cancel")}
        onCancel={() => setLeave("")}
        onOk={leave === "whatsapp" ? connectWhatsapp : connectInstagram}
        confirmLoading={Boolean(busy)}
      >
        <p>{t(leave === "whatsapp" ? "sonic_prospect.wa_leave_body" : "sonic_prospect.ig_oauth_leave_body")}</p>
      </Modal>
      <Modal
        open={Boolean(removeId)}
        title={t("sonic_prospect.channel_remove_title")}
        okText={t("sonic_prospect.channel_remove")}
        cancelText={t("sonic_prospect.confirm_cancel")}
        onCancel={() => setRemoveId("")}
        onOk={removeConnection}
        confirmLoading={Boolean(busy)}
      >
        <p>{t("sonic_prospect.channel_remove_body")}</p>
      </Modal>
    </section>
  );
};

export function ChannelIconRow({ bot, t }) {
  const icons = {
    web: GlobalOutlined,
    instagram: InstagramOutlined,
    whatsapp: WhatsAppOutlined,
  };
  return (
    <span className={styles.channelIconRow}>
      {Object.keys(icons).map((id) => {
        const Icon = icons[id];
        const status = getChannelStatus(bot, id);
        return (
          <Tooltip
            key={id}
            title={`${t(`sonic_prospect.channel_name_${id}`)} · ${t(`sonic_prospect.channel_status_${status}`)}`}
          >
            <Icon className={styles[`channelTint_${status}`] || styles.channelTint_off} />
          </Tooltip>
        );
      })}
    </span>
  );
}

export default ChannelsSection;
